import {
  CasteCategoryRecord,
  CoverageTypeRecord,
  GovernmentSchemeRecord,
  NationalityRecord,
  PatientFileRecord,
  ReligionRecord,
} from "@/types/emr/patient/patientMasterData";
import { Permissions } from "@/types/emr/permission/permission";
import { TagConfig } from "@/types/emr/tagConfig/tagConfig";
import { Organization } from "@/types/organization/organization";
import { PatientIdentifier } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";
import { UserReadMinimal } from "@/types/user/user";

export type EmergencyContactRelationship =
  | "spouse"
  | "parent"
  | "child"
  | "sibling"
  | "friend"
  | "guardian"
  | "other";

export type BplAplCategory = "bpl" | "apl" | "not_applicable";

export enum BloodGroupChoices {
  A_negative = "A_negative",
  A_positive = "A_positive",
  B_negative = "B_negative",
  B_positive = "B_positive",
  AB_negative = "AB_negative",
  AB_positive = "AB_positive",
  O_negative = "O_negative",
  O_positive = "O_positive",
  Unknown = "unknown",
}

export type GenderChoices = "male" | "female" | "others";
export type PatientRelationshipType = "father" | "mother" | "spouse";
export type PatientMaritalStatus =
  | "single"
  | "married"
  | "divorced"
  | "widowed"
  | "separated";

export interface PatientIdentifierCreate {
  config: string;
  value: string;
}

export interface PatientInsuranceRead {
  id?: string;

  coverage_type?: CoverageTypeRecord | null;
  government_scheme?: GovernmentSchemeRecord | null;

  government_scheme_other?: string | null;

  insurance_provider_name?: string | null;
  tpa_name?: string | null;

  policy_or_card_number?: string | null;

  policy_validity_date?: string | null;

  pre_authorization_required?: boolean | null;

  insurance_card_letter_file?: PatientFileRecord | null;
}

export interface PatientInsuranceWrite {
  id?: string;

  coverage_type?: string | null;
  government_scheme?: string | null;

  government_scheme_other?: string | null;

  insurance_provider_name?: string | null;
  tpa_name?: string | null;

  policy_or_card_number?: string | null;

  policy_validity_date?: string | null;

  pre_authorization_required?: boolean | null;

  insurance_card_letter_file?: string | null;
}

export interface PatientBase {
  name: string;
  gender: GenderChoices;
  phone_number: string;
  alternate_phone_number?: string;
  email?: string;
  preferred_language?: string;
  emergency_phone_number?: string;
  emergency_contact_person_name?: string;
  emergency_contact_relationship?: EmergencyContactRelationship;
  emergency_contact_relationship_other?: string;
  address?: string;
  permanent_address?: string;
  pincode?: number;
  deceased_datetime?: string | null;
  blood_group?: BloodGroupChoices;
  religion?: ReligionRecord | null;
  religion_other?: string;
  caste_category?: CasteCategoryRecord | null;
  caste_category_other?: string;
  bpl_apl_category?: BplAplCategory;
  occupation?: string;
  nationality?: NationalityRecord | null;
  patient_photo_file?: PatientFileRecord | null;
  id_proof_document_file?: PatientFileRecord | null;
  abha_id_document_file?: PatientFileRecord | null;
  date_of_birth?: string | null;
  relationship_type?: PatientRelationshipType;
  relationship_name?: string;
  marital_status?: PatientMaritalStatus;
}

export interface PatientListRead extends PatientBase {
  id: string;
  year_of_birth: number | null;
  created_date: string;
  modified_date: string;
  instance_tags: TagConfig[];
  facility_tags: TagConfig[];
  latest_encounter_identifier?: string | null;
}

export interface PatientRead extends PatientListRead, Permissions {
  geo_organization?: Organization;
  created_by?: UserReadMinimal;
  updated_by?: UserReadMinimal;
  instance_identifiers: PatientIdentifier[];
  facility_identifiers: PatientIdentifier[];
  insurance_records: PatientInsuranceRead[];
}

type PatientWriteMasterFields = {
  religion?: string;
  caste_category?: string;
  nationality?: string;
  patient_photo_file?: string;
  id_proof_document_file?: string;
  abha_id_document_file?: string;
};

export interface PatientUpdate
  extends
    Omit<
      PatientBase,
      | "religion"
      | "caste_category"
      | "nationality"
      | "patient_photo_file"
      | "id_proof_document_file"
      | "abha_id_document_file"
    >,
    PatientWriteMasterFields {
  age?: number;
  geo_organization?: string;
  identifiers: PatientIdentifierCreate[];
  insurance_records?: PatientInsuranceWrite[];
}

export interface PatientCreate extends PatientUpdate {
  facility?: string;
  tags?: string[];
}

export interface PartialPatientModel {
  id: string;
  gender: GenderChoices;
  name: string;
  phone_number: string;
  partial_id: string;
}

export interface PatientSearchResponse {
  partial: boolean;
  results: PartialPatientModel[] | PatientRead[];
}

export interface PatientSearchRequest {
  phone_number?: string;
  config?: string;
  value?: string;
  page_size?: number;
  facility?: string;
}

export interface PatientSearchRetrieveRequest {
  phone_number: string;
  year_of_birth: string;
  partial_id: string;
}

export function getPartialId(patient: PartialPatientModel | PatientListRead) {
  if ("partial_id" in patient) {
    return patient.partial_id;
  }
  return patient.id.slice(0, 5);
}

export interface PublicPatientRead {
  id: string;
  name: string;
  gender: GenderChoices;
  phone_number: string;
  emergency_phone_number: string;
  address: string;
  pincode: number | null;
  date_of_birth: string | null;
  year_of_birth: number;
  geo_organization: Organization | null;
  blood_group: BloodGroupChoices | null;
}

export interface PublicPatientCreate {
  name: string;
  gender: GenderChoices;
  date_of_birth?: string;
  age?: number;
  address: string;
  emergency_phone_number?: string;
  pincode?: number;
  geo_organization: string;
}
